import React, { useState } from 'react';
import { CASE_STUDIES } from '../constants';
import { AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';

export const CaseStudies: React.FC = () => {
  const [activeCase, setActiveCase] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{msg: string, safe: boolean} | null>(null);

  const handleChoice = (isSafe: boolean, fb: string) => {
    setFeedback({ msg: fb, safe: isSafe });
  };

  const reset = () => {
    setActiveCase(null);
    setFeedback(null);
  };

  if (activeCase) {
    const scenario = CASE_STUDIES.find(c => c.id === activeCase)!;
    return (
      <div className="max-w-3xl mx-auto p-6">
        <button onClick={reset} className="text-slate-500 mb-4 hover:underline">&larr; Back to Cases</button>
        <div className="bg-white p-8 rounded-xl shadow-xl">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="text-orange-500" /> {scenario.title}
          </h2>
          <p className="text-lg mb-8 bg-slate-50 p-4 rounded border-l-4 border-orange-300">{scenario.scenario}</p>
          
          {!feedback ? (
            <div className="space-y-4">
              <h3 className="font-bold text-slate-700">What do you do?</h3>
              {scenario.options.map((opt, i) => (
                <button 
                  key={i}
                  onClick={() => handleChoice(opt.isSafe, opt.feedback)}
                  className="w-full text-left p-4 rounded-lg border hover:bg-blue-50 hover:border-blue-400 transition-all"
                >
                  {opt.text}
                </button>
              ))}
            </div>
          ) : (
            <div className={`p-6 rounded-xl text-center animate-fade-in ${feedback.safe ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {feedback.safe ? <ThumbsUp className="mx-auto mb-2 w-12 h-12"/> : <ThumbsDown className="mx-auto mb-2 w-12 h-12"/>}
              <h3 className="text-2xl font-bold mb-2">{feedback.safe ? 'Good Choice!' : 'Oops!'}</h3>
              <p className="text-lg">{feedback.msg}</p>
              <button onClick={reset} className="mt-6 px-6 py-2 bg-white rounded shadow text-sm font-bold">Try Another Case</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <h2 className="text-3xl font-bold mb-8 text-cyber-dark">Real-World Scenarios</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CASE_STUDIES.map(c => (
          <div 
            key={c.id} 
            onClick={() => setActiveCase(c.id)}
            className="bg-white p-6 rounded-xl shadow hover:shadow-lg cursor-pointer transition-transform hover:-translate-y-1"
          >
            <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="text-orange-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">{c.title}</h3>
            <p className="text-slate-500 text-sm">Test your decision making.</p>
          </div>
        ))}
      </div>
    </div>
  );
};
